/**
 * Content filtering utilities for AI response processing
 *
 * This module provides functions to filter and clean AI-generated content
 * before copying to clipboard or other operations.
 */

import { createLogger } from "./logger";

const log = createLogger("ContentFilters");

/**
 * Strips metadata sections (Prompt Coach and Assumptions Summary) from AI response content.
 *
 * These sections are valuable for displaying to users in the UI but should not be included
 * when copying content to Word or other destinations. This function removes:
 * - "Prompt Coach" section (any header level) and all content following it
 * - "Assumptions Summary" section (any header level) and all content following it
 * - The horizontal separator (---) if present before these sections
 * - Any blank lines before the sections for clean output
 *
 * The function trusts backend output format - since we control agent responses, any occurrence
 * of these metadata headers is a true metadata section (no false positives possible).
 *
 * @param content - The full AI response content (markdown format)
 * @returns The content with metadata sections removed, or original content if no sections found
 *
 * @example
 * ```typescript
 * const response = `# Contract Section
 *
 * This is the main content.
 *
 *
 * ---
 *
 * #### Assumptions Summary
 * Some assumptions here.
 *
 * #### Prompt Coach
 * Some coaching tips here.`;
 *
 * const cleaned = stripMetadataSections(response);
 * // Returns: "# Contract Section\n\nThis is the main content."
 * ```
 */
export function stripMetadataSections(content: string): string {
  // Handle edge cases - return empty string for non-string or falsy values
  if (typeof content !== "string") {
    return "";
  }

  if (!content) {
    return "";
  }

  // Trim to normalize trailing whitespace
  const trimmedContent = content.trim();

  if (trimmedContent.length === 0) {
    return "";
  }

  /**
   * Pattern explanation:
   * - \n+          : One or more newlines before section (captures any blank lines that exist)
   * - (?:---\n+)?  : Optional separator (--- followed by newlines) - non-capturing group
   *                  Makes pattern work with or without markdown separator
   * - #+           : One or more # symbols (matches ANY header level H1-H6)
   *                  Handles variations: ## (H2), ### (H3), #### (H4), etc.
   * - \s+          : Required whitespace after header markers
   * - (Assumptions Summary|Prompt Coach) : Either section header name
   * - [\s\S]*$    : Everything from here to end of string (including newlines)
   * - /i           : Case-insensitive flag - matches any case variation
   *                  (PROMPT COACH, Prompt Coach, prompt coach, etc.)
   *
   * The pattern matches all backend agent variations:
   * - General Agent (CCM Space): ## PROMPT COACH (no separator, H2, uppercase)
   * - Drafting Assistant: #### Assumptions Summary / #### Prompt Coach (with ---, H4, title case)
   * - Redline Analyzer: #### Prompt Coach (with ---, H4, title case)
   * - Contract Q&A: #### Prompt Coach (with ---, H4, title case)
   *
   * NOTE: No minimum blank line requirement - backend controls when these headers appear,
   * so any occurrence is a metadata section. If blank lines exist before the section,
   * they are included in the stripped content for clean output.
   */
  const metadataSectionPattern = /\n+(?:---\n+)?#+\s+(Assumptions Summary|Prompt Coach)[\s\S]*$/i;

  // Attempt to strip metadata sections (runs regex once for performance)
  const cleanedContent = trimmedContent.replace(metadataSectionPattern, "");
  const wasFiltered = cleanedContent !== trimmedContent;

  // If no metadata sections were found, return original content
  if (!wasFiltered) {
    log.debug("No Prompt Coach or Assumptions Summary sections found in content");
    return content;
  }

  // Metadata sections were successfully stripped
  log.info("Successfully stripped Prompt Coach and Assumptions Summary sections from content");

  // Remove any trailing whitespace after stripping sections
  return cleanedContent.trimEnd();
}

/**
 * Checks if content contains metadata sections (Prompt Coach or Assumptions Summary).
 *
 * Useful for determining whether filtering is needed before performing
 * expensive operations or for conditional UI rendering.
 *
 * @param content - The content to check
 * @returns true if metadata sections are present, false otherwise
 *
 * @example
 * ```typescript
 * if (hasMetadataSections(response)) {
 *   console.log("This response includes guidance sections");
 * }
 * ```
 */
export function hasMetadataSections(content: string): boolean {
  if (!content || typeof content !== "string") {
    return false;
  }

  // Same pattern as stripMetadataSections but without capturing everything to end
  // Matches: 1+ newlines, optional separator, any header level, section name (case-insensitive)
  // No minimum blank line requirement - backend controls output
  const metadataSectionPattern = /\n+(?:---\n+)?#+\s+(Assumptions Summary|Prompt Coach)/i;
  return metadataSectionPattern.test(content);
}
